import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { MdOutlineArrowRightAlt } from "react-icons/md";
import { BiSolidRightArrow } from "react-icons/bi";

const AgentCard = ({ agent, onStartCaseReview, user, onAuthRequired, style, onMouseMove, onMouseLeave, index, onAgentSelect }) => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);

  const agentIdMapping = {
    1: 'family-based',
    2: 'work-visa',
    3: 'asylum-protection', 
    4: 'student-visa',
    5: 'citizenship',
    6: 'rfe-noid',
    7: 'daca-tps',
    8: 'visitor-tourist'
  };

  const agentId = agentIdMapping[agent.id] || 'family-based';

  useEffect(() => {
    checkAgentAccess();
  }, [authUser, agent.id]);

  useEffect(() => {
    console.log(`Agent ${agentId} state update: hasAccess=${hasAccess}, loading=${loading}, accessInfo=`, accessInfo);
  }, [hasAccess, loading, accessInfo]);

  const checkAgentAccess = async () => {
    if (!authUser) {
      setHasAccess(true); // Allow viewing for non-authenticated users
      setLoading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/subscription/check-agent-access/${agentId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Agent ${agentId} access check:`, data);
        console.log(`Setting hasAccess to:`, data.has_access);
        setHasAccess(data.has_access);
        setAccessInfo(data);
      } else {
        console.error(`Failed to check access for agent ${agentId}:`, response.status);
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Error checking agent access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    console.log(`Agent ${agentId} clicked - hasAccess: ${hasAccess}, authUser: ${!!authUser}`);
    
    if (!authUser) {
      onAuthRequired();
      return;
    }

    // For authenticated users, always open the agent modal first
    // The modal will handle access control for specific features (Ask Questions vs Review My Case)
    console.log(`Agent ${agentId} - Opening agent modal`);
    if (onAgentSelect) {
      onAgentSelect(agent);
    } else {
      // Fallback to old behavior if onAgentSelect not provided
      if (!hasAccess) {
        console.log(`Agent ${agentId} - Access denied, showing upgrade prompt`);
        handleUpgradePrompt();
        return;
      }
      console.log(`Agent ${agentId} - Access granted, starting case review`);
      onStartCaseReview(agent);
    }
  };

  const handleUpgradePrompt = () => {
    const subscriptionName = accessInfo?.subscription_name || 'Explorer';
    
    const message = subscriptionName === 'Explorer' 
      ? `Your ${subscriptionName} plan allows 1 agent. ${accessInfo?.reason || 'Upgrade to access more agents.'}`
      : `Your ${subscriptionName} plan: ${accessInfo?.reason || 'This agent is not selected.'}`;

    if (window.confirm(`${message}\n\nWould you like to upgrade or manage your agents?`)) {
      if (subscriptionName === 'Navigator' || subscriptionName === 'Advocate') {
        // Trigger agent selection
        window.location.reload();
      } else {
        navigate('/pricing');
      }
    }
  };

  return (
    <div
      onMouseMove={(e) => onMouseMove(e, index)}
      onMouseLeave={() => onMouseLeave(index)}
      style={style}
      className={`relative bg-transparent backdrop-blur-lg backdrop-saturate-150 p-6 rounded-xl border border-white/10 shadow-[0_0_15px_rgba(0,251,166,0.1)] transition-all duration-200 ease-in-out flex flex-col will-change-transform ${
        !hasAccess && authUser ? 'opacity-60' : ''
      }`}
    >
      {/* Pulse indicator - modified for locked agents */}
      <span
        className={`absolute top-4 right-4 w-2 h-2 rounded-full ${
          authUser && !loading && !hasAccess ? 'bg-red-500' : 'bg-[#00fba6]'
        }`}
        style={{
          animation: "pulseGlow 1.5s ease-in-out infinite",
          filter: "blur(0.5px)",
          boxShadow: authUser && !loading && !hasAccess 
            ? "0 0 8px rgba(239, 68, 68, 0.4)" 
            : "0 0 8px rgba(0, 251, 166, 0.4)",
        }}
      ></span>

      {/* Lock Icon for Inaccessible Agents */}
      {authUser && !loading && !hasAccess && (
        <div className="absolute top-2 right-8 text-xl">
          🔒
        </div>
      )}

      {/* Loading Indicator */}
      {loading && authUser && (
        <div className="absolute top-2 right-8">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <h2 className="text-lg font-bold text-[#00fba6] flex items-center gap-2">
        <span>{agent.icon}</span> {agent.name}
      </h2>

      <p className="text-md text-gray-400 mt-3 italic text-left">
        {agent.description}
      </p>

      <div className="mt-4 space-y-3 text-white text-sm list-disc list-inside max-[600px]:text-lg">
        {agent.features.map((feature, i) => (
          <p key={i} className="flex items-center gap-2">
            <BiSolidRightArrow size="10" color="#00fba6" />
            {feature}
          </p>
        ))}
      </div>

      {/* Access Status for authenticated users */}
      {authUser && !loading && (
        <div className="mt-4 text-xs px-2 py-1 rounded text-center bg-green-900/50 text-green-400">
          ✓ Available
        </div>
      )}

      <div className="mt-auto" />
      <button
        onClick={handleClick}
        className="mt-8 w-fit font-semibold px-5 py-2 rounded-full text-sm transition flex items-center gap-2 bg-gradient-to-r from-[#00fba6] to-[#00d9f1] text-black hover:brightness-110 shadow-[0_0_10px_#00fba6]"
      >
        {!authUser ? (
          <>SIGN IN TO ACCESS <MdOutlineArrowRightAlt /></>
        ) : (
          <>TRY NOW <MdOutlineArrowRightAlt /></>
        )}
      </button>
    </div>
  );
};

export default AgentCard;